import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { salesPointAPI, SalesPoint } from "../../services/salesPointAPI";
import { branchAPI } from "../../services/branchAPI";
import { workCategoryLabel } from "../../constants/workCategories";
import { useRBAC } from "../../hooks/useRBAC";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import SortableTableCell from "../../components/layout/SortableTableCell";
import TableActionButton from "../../components/layout/TableActionButton";
import { useTableSort } from "../../hooks/useTableSort";

type SalesPointSortField = "name" | "description" | "branch" | "createdAt";

/** Column width follows content; table scrolls horizontally if needed */
const fitContentCell = {
  width: "1%",
  whiteSpace: "nowrap" as const,
};

const SalesPointsPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState<string>("all");
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    salesPoint: SalesPoint | null;
  }>({ open: false, salesPoint: null });
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { canCreate, canUpdate, canDelete, canRead } = useRBAC();
  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<SalesPointSortField>("name");
  const onSort = (field: SalesPointSortField) => handleSort(field, () => setPage(0));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    data: salesPointsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["sales_points", page, rowsPerPage, debouncedSearch, branchFilter, sortBy, sortOrder],
    queryFn: () =>
      salesPointAPI.getSalesPoints({
        pageNo: page + 1,
        pageSize: rowsPerPage,
        ...sortParams,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(branchFilter !== "all" ? { branchId: branchFilter } : {}),
      }),
  });

  const { data: branches = [] } = useQuery({
    queryKey: ["branches", "all"],
    queryFn: () => branchAPI.getAllBranches(),
  });

  const deleteSalesPointMutation = useMutation({
    mutationFn: salesPointAPI.deleteSalesPoint,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales_points"] });
      setDeleteDialog({ open: false, salesPoint: null });
      setAlert({ type: "success", message: "Sales point deleted successfully" });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to delete salesPoint",
      });
    },
  });

  const salesPoints = salesPointsData?.data || [];
  const totalCount = salesPointsData?.total || 0;

  const filterDropdowns = (
    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 200 } }}>
      <InputLabel>Branch</InputLabel>
      <Select
        value={branchFilter}
        label="Branch"
        onChange={(e) => {
          setBranchFilter(e.target.value);
          setPage(0);
        }}
      >
        <MenuItem value="all">All Branches</MenuItem>
        {branches.map((branch) => (
          <MenuItem key={branch.id} value={branch.id}>
            {branch.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box>
      <PageHeader
        title="Sales Points"
        subtitle="Manage sales points across branches"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: "Sales Points" },
        ]}
        action={
          <>
            <Tooltip title="Export PDF">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Excel">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <ExcelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canCreate("sales_points") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/sales-points/new")}
              >
                Add SalesPoint
              </Button>
            )}
          </>
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : "Failed to load sales points"}
        </Alert>
      )}

      <DataListCard
        searchPlaceholder="Search name or description…"
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        filters={filterDropdowns}
        footer={
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ width: "100%" }}
          />
        }
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography color="text.secondary">Loading sales points…</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: "auto", width: "100%" }}>
            <Table
              size="small"
              sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}
            >
              <TableHead>
                <TableRow>
                  <TableCell width={72} sx={fitContentCell}>
                    S.No.
                  </TableCell>
                  <SortableTableCell
                    label="Sales point"
                    field="name"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    sx={fitContentCell}
                  />
                  <TableCell sx={fitContentCell}>Phone</TableCell>
                  <TableCell sx={fitContentCell}>GSTIN</TableCell>
                  <TableCell sx={fitContentCell}>Licence</TableCell>
                  <TableCell sx={fitContentCell}>Tax Mode</TableCell>
                  <SortableTableCell
                    label="Description"
                    field="description"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    sx={fitContentCell}
                  />
                  <SortableTableCell
                    label="Branch"
                    field="branch"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    sx={fitContentCell}
                  />
                  <SortableTableCell
                    label="Created"
                    field="createdAt"
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={onSort}
                    sx={fitContentCell}
                  />
                  <TableCell align="right" sx={fitContentCell}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salesPoints.map((salesPoint, index) => (
                  <TableRow key={salesPoint.id} hover>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2" color="text.secondary">
                        {page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2" fontWeight={600}>
                        {salesPoint.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {salesPoint.address || "Sales point"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2">
                        {salesPoint.phoneNo || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2">
                        {salesPoint.gstin || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2">
                        {salesPoint.licence || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2">
                        {salesPoint.taxMode === "IGST"
                          ? "IGST"
                          : salesPoint.taxMode === "CGST_SGST"
                            ? "CGST + SGST"
                            : salesPoint.taxMode || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {workCategoryLabel(salesPoint.workstationType)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2" color="text.secondary">
                        {salesPoint.description || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2">
                        {salesPoint.branch?.name ||
                          branches.find((b) => b.id === salesPoint.branchId)?.name ||
                          "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={fitContentCell}>
                      <Typography variant="body2" color="text.secondary">
                        {salesPoint.createdAt
                          ? format(new Date(salesPoint.createdAt), "dd MMM yyyy")
                          : "—"}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={fitContentCell}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {canRead("sales_points") && (
                          <TableActionButton
                            title="View salesPoint"
                            onClick={() => navigate(`/sales-points/${salesPoint.id}`)}
                          >
                            <ViewIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {canUpdate("sales_points") && (
                          <TableActionButton
                            title="Edit salesPoint"
                            variant="primary"
                            onClick={() => navigate(`/sales-points/${salesPoint.id}/edit`)}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                        {canDelete("sales_points") && (
                          <TableActionButton
                            title="Delete salesPoint"
                            variant="danger"
                            onClick={() => setDeleteDialog({ open: true, salesPoint })}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </TableActionButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {salesPoints.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No sales points found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataListCard>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, salesPoint: null })}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete salesPoint</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteDialog.salesPoint?.name}</strong>? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, salesPoint: null })} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() =>
              deleteDialog.salesPoint && deleteSalesPointMutation.mutate(deleteDialog.salesPoint.id)
            }
            color="error"
            variant="contained"
            disabled={deleteSalesPointMutation.isPending}
          >
            {deleteSalesPointMutation.isPending ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesPointsPage;
